import axios from "axios";

// Bridge between the plain apiService (no React context) and react-auth-kit.
// apiService calls refreshSession() when a request fails with an expired token
// (401) or a stale-permission 403; on success it gets a brand-new access token,
// applies it to react-auth-kit's store (so future requests use it), and returns
// it so the caller can immediately retry. This is what makes a plan/role change
// seamless — the client picks up new permissions without a forced re-login.

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

type ApplyTokensFn = (data: {
  accessToken: string;
  refreshToken: string;
  account: any;
}) => void;

// Registered once by <AuthBridge> in App so we can update react-auth-kit's
// cookie/state via useSignIn from outside React.
let applyTokens: ApplyTokensFn | null = null;
export function registerApplyTokens(fn: ApplyTokensFn | null): void {
  applyTokens = fn;
}

// De-dupe: a burst of requests can 401/403 at once; we only want one refresh.
let inflight: Promise<string | null> | null = null;

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Exchange the stored refresh token for a fresh access token. Returns the new
 * access token (also applied to react-auth-kit) or null if refresh isn't
 * possible (no/expired/invalid refresh token) — in which case the caller should
 * fall back to the re-login prompt.
 */
export function refreshSession(): Promise<string | null> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // react-auth-kit stores the refresh token in the `${authName}_refresh`
      // cookie (authName is "_auth"). Not httpOnly, so readable here.
      const refreshToken = readCookie("_auth_refresh");
      if (!refreshToken) return null;
      const res = await axios.post(`${apiUrl}/api/auth/refresh`, { refreshToken });
      if (!res.data?.accessToken) return null;
      applyTokens?.({
        accessToken: res.data.accessToken,
        refreshToken: res.data.refreshToken,
        account: res.data.account,
      });
      return res.data.accessToken as string;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
